/**
 * Debouncing Hooks for Filter Operations
 * 
 * Provides efficient debouncing for filter updates while maintaining
 * excellent UX with immediate visual feedback and loading states.
 * 
 * Features:
 * - Value debouncing for input fields
 * - Callback debouncing for complex operations
 * - Loading state management
 * - Cancel previous operations
 * - Smart batching for multiple changes
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { log } from '@/utils/logger';

// Logger for debouncing operations
const logger = log.component('Debouncing');

/**
 * Generic value debouncing hook
 * Delays updating the returned value until the input stops changing
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Callback debouncing hook with loading state
 * Perfect for expensive operations like filter updates
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  deps: React.DependencyList = []
): {
  debouncedCallback: T;
  isLoading: boolean;
  cancel: () => void;
} {
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callbackRef = useRef(callback);

  // Update callback ref when dependencies change
  useEffect(() => {
    callbackRef.current = callback;
  }, deps);

  // Cancel function
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
      setIsLoading(false);
      logger.debug('Debounced operation cancelled');
    }
  }, []);

  // Debounced callback
  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      // Cancel previous timeout
      cancel();
      
      setIsLoading(true);
      logger.debug('Debounced operation scheduled', { delay, args: args.length });

      timeoutRef.current = setTimeout(async () => {
        try {
          const startTime = performance.now();
          await callbackRef.current(...args);
          const duration = performance.now() - startTime;
          
          logger.debug('Debounced operation completed', { 
            duration: `${duration.toFixed(2)}ms` 
          });
        } catch (error) {
          logger.error('Debounced operation failed', error);
        } finally {
          setIsLoading(false);
          timeoutRef.current = undefined;
        }
      }, delay);
    }) as T,
    [delay, cancel]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    debouncedCallback,
    isLoading,
    cancel
  };
}

/**
 * Filter-specific debouncing hook with smart batching
 * Combines multiple filter changes into a single update
 */
export function useDebouncedFilterUpdate(
  delay: number = 300
): {
  scheduleFilterUpdate: (updateFn: () => void, filterId?: string) => void;
  isUpdating: boolean;
  cancel: () => void;
  pendingUpdates: number;
} {
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const pendingOperationsRef = useRef<Map<string, () => void>>(new Map());

  // Cancel all pending updates
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    
    pendingOperationsRef.current.clear();
    setPendingUpdates(0);
    setIsUpdating(false);
    
    logger.debug('All filter updates cancelled');
  }, []);

  // Schedule a filter update with smart batching
  const scheduleFilterUpdate = useCallback((updateFn: () => void, filterId: string = 'default') => {
    // Add or update the operation for this filter
    pendingOperationsRef.current.set(filterId, updateFn);
    setPendingUpdates(pendingOperationsRef.current.size);
    
    // Cancel previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsUpdating(true);
    logger.debug('Filter update scheduled', { 
      filterId, 
      totalPending: pendingOperationsRef.current.size,
      delay 
    });

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      const operations = Array.from(pendingOperationsRef.current.values());
      const operationCount = operations.length;
      
      logger.debug('Executing batched filter updates', { 
        operationCount,
        filterIds: Array.from(pendingOperationsRef.current.keys())
      });

      try {
        const startTime = performance.now();
        
        // Execute all pending operations
        for (const operation of operations) {
          await operation();
        }
        
        const duration = performance.now() - startTime;
        logger.info('Batched filter updates completed', { 
          operationCount,
          duration: `${duration.toFixed(2)}ms`,
          averagePerOperation: `${(duration / operationCount).toFixed(2)}ms`
        });
        
      } catch (error) {
        logger.error('Batched filter updates failed', error);
      } finally {
        // Clean up
        pendingOperationsRef.current.clear();
        setPendingUpdates(0);
        setIsUpdating(false);
        timeoutRef.current = undefined;
      }
    }, delay);
  }, [delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    scheduleFilterUpdate,
    isUpdating,
    cancel,
    pendingUpdates
  };
}

/**
 * Search-specific debouncing hook
 * Optimized for search input with immediate visual feedback
 */
export function useDebouncedSearch(
  initialValue: string = '',
  delay: number = 300
): {
  searchValue: string;
  debouncedSearchValue: string;
  setSearchValue: (value: string) => void;
  isSearching: boolean;
  clearSearch: () => void;
} {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchValue = useDebounce(searchValue, delay);

  // Track when search is in progress
  useEffect(() => {
    if (searchValue !== debouncedSearchValue) {
      setIsSearching(true);
      logger.debug('Search in progress', { 
        current: searchValue, 
        debounced: debouncedSearchValue 
      });
    } else {
      setIsSearching(false);
      if (debouncedSearchValue) {
        logger.debug('Search completed', { query: debouncedSearchValue });
      }
    }
  }, [searchValue, debouncedSearchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    logger.debug('Search cleared');
  }, []);

  return {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
    isSearching,
    clearSearch
  };
}

/**
 * Range input debouncing hook (for sliders)
 * Optimized for continuous range updates with visual feedback
 */
export function useDebouncedRange(
  initialRange: [number, number],
  delay: number = 150
): {
  range: [number, number];
  debouncedRange: [number, number];
  setRange: (range: [number, number]) => void;
  isChanging: boolean;
} {
  const [range, setRange] = useState(initialRange);
  const [isChanging, setIsChanging] = useState(false);
  const debouncedRange = useDebounce(range, delay);

  // Track when range is changing
  useEffect(() => {
    const isRangeChanging = range[0] !== debouncedRange[0] || range[1] !== debouncedRange[1];
    setIsChanging(isRangeChanging);
    
    if (isRangeChanging) {
      logger.debug('Range changing', { current: range, debounced: debouncedRange });
    } else if (range[0] !== initialRange[0] || range[1] !== initialRange[1]) {
      logger.debug('Range update completed', { finalRange: debouncedRange });
    }
  }, [range, debouncedRange, initialRange]);

  return {
    range,
    debouncedRange,
    setRange,
    isChanging
  };
}